import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const SECTIONS = [
    {
        id: 'four-noble-truths',
        title: 'The Four Noble Truths',
        sanskrit: 'Catvāri Āryasatyāni',
        tibetan: 'འཕགས་པའི་བདེན་པ་བཞི།',
        icon: (
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M12 3v1m0 16v1M4.22 4.22l.707.707m12.727 12.727.707.707M3 12H2m20 0h-1M4.22 19.78l.707-.707m12.727-12.727.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
            </svg>
        ),
        color: 'amber',
        summary: 'The foundation of all Buddhist teaching — the Buddha\'s first discourse after his enlightenment.',
        items: [
            {
                label: 'Dukkha — The Truth of Suffering',
                detail: 'Life is characterized by unsatisfactoriness. Birth, aging, sickness, and death involve suffering. Not getting what we want, losing what we love, being bound to what we dislike — all of this is dukkha. Even pleasurable experiences carry the seed of suffering because they are impermanent.',
            },
            {
                label: 'Samudāya — The Truth of the Origin of Suffering',
                detail: 'Suffering arises from craving (tanhā). We crave pleasures, existence, and non-existence. This craving is fueled by ignorance of the true nature of reality — that all phenomena are impermanent, unsatisfactory, and without inherent self.',
            },
            {
                label: 'Nirodha — The Truth of the Cessation of Suffering',
                detail: 'It is possible to end suffering completely. When craving is abandoned without remainder, liberated, released, given up, and relinquished — this is nibbāna, the unconditioned peace beyond birth and death.',
            },
            {
                label: 'Magga — The Truth of the Path',
                detail: 'The Noble Eightfold Path is the way to the cessation of suffering. It is the Middle Way between extreme asceticism and indulgence — a path of wisdom, ethical conduct, and mental cultivation.',
            },
        ],
    },
    {
        id: 'eightfold-path',
        title: 'The Noble Eightfold Path',
        sanskrit: 'Āryāṣṭāṅgamārga',
        tibetan: 'འཕགས་པའི་ལམ་ཡན་ལག་བརྒྱད།',
        icon: (
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
        ),
        color: 'orange',
        summary: 'The practical path to liberation, grouped into three trainings: Wisdom, Ethical Conduct, and Meditation.',
        items: [
            {
                label: 'Right View — Sammā Diṭṭhi',
                detail: 'Understanding the Four Noble Truths, the nature of karma, and the reality of impermanence, suffering, and non-self. Right View is both the beginning and the culmination of the path.',
            },
            {
                label: 'Right Intention — Sammā Saṅkappa',
                detail: 'Intentions of renunciation (letting go of desire), goodwill (freedom from ill will), and harmlessness (freedom from cruelty). These intentions purify and orient the mind toward liberation.',
            },
            {
                label: 'Right Speech — Sammā Vācā',
                detail: 'Abstaining from false speech, divisive speech, harsh speech, and idle chatter. Speaking what is true, helpful, timely, gentle, and connected with the Dharma.',
            },
            {
                label: 'Right Action — Sammā Kammanta',
                detail: 'Abstaining from killing living beings, from stealing, and from sexual misconduct. Living in a way that causes no harm and supports the well-being of all sentient beings.',
            },
            {
                label: 'Right Livelihood — Sammā Ājīva',
                detail: 'Earning one\'s living in a way that does not cause harm — avoiding trades in weapons, living beings, meat, alcohol, and poisons.',
            },
            {
                label: 'Right Effort — Sammā Vāyāma',
                detail: 'The four great efforts: preventing unwholesome states from arising, abandoning those that have arisen, cultivating wholesome states, and maintaining those that have arisen.',
            },
            {
                label: 'Right Mindfulness — Sammā Sati',
                detail: 'Clear, sustained awareness of the body, feelings, mind states, and mental objects. The foundation of all meditative development and insight practice.',
            },
            {
                label: 'Right Concentration — Sammā Samādhi',
                detail: 'The four jhānas — progressively deeper states of meditative absorption. The mind becomes unified, still, luminous, and equanimous, forming the basis for liberating insight.',
            },
        ],
    },
    {
        id: 'three-jewels',
        title: 'The Three Jewels',
        sanskrit: 'Triratna',
        tibetan: 'དཀོན་མཆོག་གསུམ།',
        icon: (
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
        ),
        color: 'yellow',
        summary: 'The Buddha, the Dharma, and the Sangha — the three refuges in which all Buddhist practitioners take refuge.',
        items: [
            {
                label: 'The Buddha — The Awakened One',
                detail: 'Siddhartha Gautama, who attained full enlightenment under the Bodhi tree in Bodh Gaya. The Buddha is also the buddha-nature within all beings — the inherent capacity for awakening that every sentient being possesses.',
            },
            {
                label: 'The Dharma — The Teaching',
                detail: 'The Buddha\'s teachings on the nature of reality, the causes of suffering, the path to liberation, and the nature of awakening. The Dharma is the raft that carries us across the ocean of samsara to the shore of nirvana.',
            },
            {
                label: 'The Sangha — The Community',
                detail: 'Traditionally, the community of ordained monastics who have attained the noble stages of the path. More broadly, all practitioners who walk the Dharma path together, supporting each other with compassion and wisdom.',
            },
        ],
    },
    {
        id: 'three-marks',
        title: 'The Three Marks of Existence',
        sanskrit: 'Trilakṣaṇa',
        tibetan: 'མཚན་ཉིད་གསུམ།',
        icon: (
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
        color: 'teal',
        summary: 'Three fundamental characteristics that the Buddha said apply to all conditioned phenomena.',
        items: [
            {
                label: 'Anicca — Impermanence',
                detail: 'All conditioned phenomena are impermanent. Every thought, sensation, emotion, relationship, and material thing arises and passes away. Recognizing impermanence is the beginning of wisdom and the antidote to attachment.',
            },
            {
                label: 'Dukkha — Unsatisfactoriness',
                detail: 'All conditioned phenomena are ultimately unsatisfying. Even pleasant experiences cannot provide lasting fulfillment because they change. This is not pessimism — it is a clear-eyed diagnosis that points toward what is truly free from suffering.',
            },
            {
                label: 'Anattā — Non-self',
                detail: 'There is no fixed, permanent, independent self among the five aggregates (form, feeling, perception, mental formations, consciousness). What we call "self" is a dynamic, interdependent process. Seeing through the illusion of self is the gateway to liberation.',
            },
        ],
    },
    {
        id: 'six-paramitas',
        title: 'The Six Perfections',
        sanskrit: 'Ṣaṭpāramitā',
        tibetan: 'ཕར་ཕྱིན་དྲུག',
        icon: (
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
        ),
        color: 'rose',
        summary: 'The six qualities a Bodhisattva cultivates on the path to full enlightenment for the benefit of all beings.',
        items: [
            {
                label: 'Dāna — Generosity',
                detail: 'The practice of giving freely — material gifts, the gift of protection from fear, and the greatest gift of all: the Dharma. Generosity loosens the grip of attachment and cultivates the open heart of the Bodhisattva.',
            },
            {
                label: 'Śīla — Ethical Conduct',
                detail: 'The practice of the precepts — refraining from harm and cultivating virtue. For the Bodhisattva, ethics flows from the recognition that all beings wish to be free from suffering, and our actions either honor or violate that wish.',
            },
            {
                label: 'Kṣānti — Patience',
                detail: 'The ability to remain stable and unshaken in the face of difficulty, harm, and the profound depth of Dharma. Śāntideva taught that a single moment of anger destroys merit accumulated over eons, while patience builds a strength no outer force can break.',
            },
            {
                label: 'Vīrya — Diligent Effort',
                detail: 'Joyful, enthusiastic energy in the practice of virtue and Dharma. Not the strain of forcing, but the natural momentum of a mind in love with the path — like a fire that burns brighter the more fuel it receives.',
            },
            {
                label: 'Dhyāna — Meditative Concentration',
                detail: 'The cultivation of a mind that is stable, clear, and unified. Through the progressive development of śamatha (calm abiding) and vipaśyanā (insight), the practitioner gains the mental stability to investigate and realize the nature of mind.',
            },
            {
                label: 'Prajñā — Wisdom',
                detail: 'The direct realization of śūnyatā (emptiness) — the understanding that all phenomena lack inherent, independent existence. Wisdom is the sword that cuts through the root of ignorance and makes liberation possible.',
            },
        ],
    },
    {
        id: 'emptiness',
        title: 'Śūnyatā — Emptiness',
        sanskrit: 'Śūnyatā',
        tibetan: 'སྟོང་པ་ཉིད།',
        icon: (
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            </svg>
        ),
        color: 'violet',
        summary: 'The most profound teaching of Mahāyāna Buddhism — that all phenomena are empty of inherent existence.',
        items: [
            {
                label: 'What Emptiness Is Not',
                detail: 'Emptiness does not mean that nothing exists. Tables, trees, people, and suffering are all real in the conventional sense. Emptiness means that nothing exists from its own side, independently, by its own power — including emptiness itself.',
            },
            {
                label: 'Dependent Origination',
                detail: 'Because phenomena are empty, they can arise dependently — in dependence upon causes, conditions, and designating minds. The Heart Sutra\'s "form is emptiness, emptiness is form" points to this: emptiness and dependent arising are the same truth from two perspectives.',
            },
            {
                label: 'The Two Truths',
                detail: 'Conventional truth (the world of appearance and experience) and ultimate truth (emptiness) are not contradictory but complementary. The Madhyamaka view holds the two truths in union — phenomena appear and function, and yet they are empty of inherent existence.',
            },
            {
                label: 'Realizing Emptiness',
                detail: 'Intellectual understanding of emptiness must be transformed through meditation into direct, non-conceptual realization. When emptiness is seen directly, the grasping at a self that is the root of all suffering is permanently loosened at its foundation.',
            },
        ],
    },
];

const COLOR_STYLES = {
    amber: {
        border: 'border-amber-500/30 hover:border-amber-400/60',
        icon: 'text-amber-400',
        badge: 'bg-amber-900/40 text-amber-300',
        dot: 'bg-amber-400',
        accent: 'text-amber-400',
        bullet: 'bg-amber-500',
    },
    orange: {
        border: 'border-orange-500/30 hover:border-orange-400/60',
        icon: 'text-orange-400',
        badge: 'bg-orange-900/40 text-orange-300',
        dot: 'bg-orange-400',
        accent: 'text-orange-400',
        bullet: 'bg-orange-500',
    },
    yellow: {
        border: 'border-yellow-500/30 hover:border-yellow-400/60',
        icon: 'text-yellow-400',
        badge: 'bg-yellow-900/40 text-yellow-300',
        dot: 'bg-yellow-400',
        accent: 'text-yellow-400',
        bullet: 'bg-yellow-500',
    },
    teal: {
        border: 'border-teal-500/30 hover:border-teal-400/60',
        icon: 'text-teal-400',
        badge: 'bg-teal-900/40 text-teal-300',
        dot: 'bg-teal-400',
        accent: 'text-teal-400',
        bullet: 'bg-teal-500',
    },
    rose: {
        border: 'border-rose-500/30 hover:border-rose-400/60',
        icon: 'text-rose-400',
        badge: 'bg-rose-900/40 text-rose-300',
        dot: 'bg-rose-400',
        accent: 'text-rose-400',
        bullet: 'bg-rose-500',
    },
    violet: {
        border: 'border-violet-500/30 hover:border-violet-400/60',
        icon: 'text-violet-400',
        badge: 'bg-violet-900/40 text-violet-300',
        dot: 'bg-violet-400',
        accent: 'text-violet-400',
        bullet: 'bg-violet-500',
    },
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ItemRow = ({ item, bullet }) => {
    const [open, setOpen] = useState(false);

    return (
        <li>
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full text-left group flex items-start gap-3 py-3 border-b border-slate-700/50 last:border-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded"
                aria-expanded={open}
            >
                <span className={`mt-2 flex-shrink-0 w-1.5 h-1.5 rounded-full ${bullet} transition-transform group-hover:scale-125`} aria-hidden="true" />
                <span className="flex-1">
                    <span className="text-gray-200 font-medium text-sm leading-relaxed">{item.label}</span>
                    {open && (
                        <span className="block mt-2 text-gray-400 text-sm leading-relaxed">
                            {item.detail}
                        </span>
                    )}
                </span>
                <svg
                    className={`flex-shrink-0 mt-1 w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
        </li>
    );
};

const SectionCard = ({ section, isActive, onToggle }) => {
    const c = COLOR_STYLES[section.color];

    return (
        <article
            id={section.id}
            className={`bg-slate-900/70 backdrop-blur-sm border ${c.border} rounded-xl transition-colors`}
        >
            {/* Header — always visible */}
            <button
                onClick={onToggle}
                className="w-full text-left px-6 py-5 flex items-center gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded-xl"
                aria-expanded={isActive}
                aria-controls={`${section.id}-body`}
            >
                <span className={`flex-shrink-0 ${c.icon}`}>{section.icon}</span>
                <span className="flex-1 min-w-0">
                    <span className="flex flex-wrap items-center gap-2 mb-0.5">
                        <span className="text-white font-semibold text-base">{section.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.badge}`}>
                            {section.sanskrit}
                        </span>
                    </span>
                    <span className="text-gray-400 text-xs">{section.tibetan}</span>
                </span>
                <svg
                    className={`flex-shrink-0 w-5 h-5 text-gray-500 transition-transform ${isActive ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Expandable body */}
            {isActive && (
                <div id={`${section.id}-body`} className="px-6 pb-6">
                    <p className="text-gray-400 text-sm mb-4 leading-relaxed border-l-2 border-slate-600 pl-3">
                        {section.summary}
                    </p>
                    <ul className="space-y-0" aria-label={`${section.title} teachings`}>
                        {section.items.map((item) => (
                            <ItemRow key={item.label} item={item} bullet={c.bullet} />
                        ))}
                    </ul>
                </div>
            )}
        </article>
    );
};

// ---------------------------------------------------------------------------
// Table of contents
// ---------------------------------------------------------------------------

const TOC = ({ sections, activeId, onSelect }) => (
    <nav
        aria-label="Jump to section"
        className="bg-slate-900/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 sticky top-4"
    >
        <p className="text-xs text-gray-500 uppercase tracking-widest mb-3 px-1">On this page</p>
        <ul className="space-y-1">
            {sections.map((s) => {
                const c = COLOR_STYLES[s.color];
                return (
                    <li key={s.id}>
                        <button
                            onClick={() => onSelect(s.id)}
                            className={`w-full text-left flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${
                                activeId === s.id
                                    ? `${c.accent} bg-slate-800`
                                    : 'text-gray-400 hover:text-gray-200 hover:bg-slate-800/50'
                            }`}
                        >
                            <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${c.dot}`} aria-hidden="true" />
                            {s.title}
                        </button>
                    </li>
                );
            })}
        </ul>
    </nav>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Learn = () => {
    const [openId, setOpenId] = useState('four-noble-truths');

    const handleToggle = (id) => {
        setOpenId(prev => prev === id ? null : id);
    };

    const handleSelect = (id) => {
        setOpenId(id);
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="min-h-screen py-12 px-6">
            <div className="max-w-5xl mx-auto">

                {/* Page header */}
                <header className="mb-10">
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl px-8 py-7 border border-slate-700/40">
                        <p className="text-amber-400 text-xs font-semibold uppercase tracking-widest mb-2">
                            Dharma Study
                        </p>
                        <h1 className="text-3xl font-bold text-white mb-3">
                            Foundations of Buddhist Teaching
                        </h1>
                        <p className="text-gray-400 text-sm leading-relaxed max-w-2xl">
                            An introduction to the core teachings that form the basis of all Buddhist practice — drawn
                            primarily from the Mahāyāna and Tibetan traditions. Select any teaching below to explore
                            its meaning and application on the path.
                        </p>
                        <div className="mt-5 flex flex-wrap gap-3">
                            <Link
                                to="/library"
                                className="inline-flex items-center gap-1.5 text-sm text-amber-400 hover:text-amber-300 transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Explore the Library
                            </Link>
                            <span className="text-slate-600" aria-hidden="true">&bull;</span>
                            <Link
                                to="/temple"
                                className="inline-flex items-center gap-1.5 text-sm text-amber-400 hover:text-amber-300 transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                                Enter the Virtual Temple
                            </Link>
                        </div>
                    </div>
                </header>

                {/* Two-column layout */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

                    {/* TOC sidebar */}
                    <aside className="lg:col-span-1 hidden lg:block">
                        <TOC sections={SECTIONS} activeId={openId} onSelect={handleSelect} />
                    </aside>

                    {/* Teaching cards */}
                    <main className="lg:col-span-3 space-y-4" aria-label="Buddhist teachings">
                        {SECTIONS.map((section) => (
                            <SectionCard
                                key={section.id}
                                section={section}
                                isActive={openId === section.id}
                                onToggle={() => handleToggle(section.id)}
                            />
                        ))}

                        {/* Footer callout */}
                        <div className="bg-slate-900/50 border border-slate-700/30 rounded-xl px-6 py-5 text-center">
                            <p className="text-gray-400 text-sm leading-relaxed mb-3">
                                These teachings are an introduction. The Dharma is vast and deep —
                                explore the Library for sutras, mantras, and Dharma works, or practice
                                in the Virtual Temple.
                            </p>
                            <div className="flex justify-center gap-4 flex-wrap">
                                <Link
                                    to="/library/mantras"
                                    className="bg-amber-600 hover:bg-amber-500 text-white text-sm px-4 py-2 rounded-full font-semibold transition-colors"
                                >
                                    Mantras
                                </Link>
                                <Link
                                    to="/library/sutras"
                                    className="bg-slate-700 hover:bg-slate-600 text-white text-sm px-4 py-2 rounded-full font-semibold transition-colors"
                                >
                                    Sutras
                                </Link>
                                <Link
                                    to="/temple"
                                    className="bg-slate-700 hover:bg-slate-600 text-white text-sm px-4 py-2 rounded-full font-semibold transition-colors"
                                >
                                    Virtual Temple
                                </Link>
                            </div>
                        </div>
                    </main>

                </div>
            </div>
        </div>
    );
};

export default Learn;
